import React, { useEffect, useState } from 'react';
import { UserConfig } from '../../lib/types';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';

interface UserConfigDialogProps {
  isOpen: boolean;
  onClose: () => void;
  config: UserConfig | null;
  onSubmit: (data: UserConfig) => Promise<void>;
}

export const UserConfigDialog: React.FC<UserConfigDialogProps> = ({
  isOpen,
  onClose,
  config,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<UserConfig>({
    id: '',
    name: '',
    bybitTestnetApiKey: '',
    bybitTestnetApiSecret: '',
    bybitProductionApiKey: '',
    bybitProductionApiSecret: '',
    testnetApiKey: '',
    productionApiKey: '',
    proxyHost: '',
    proxyPort: undefined,
    proxyUsername: '',
    proxyPassword: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (config) {
      setFormData(config);
    }
  }, [config]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error saving config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof UserConfig) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">
          Edit Trading Configuration - {config?.name}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <h3 className="font-medium text-gray-700">Testnet Configuration</h3>
            
            <div>
              <Label htmlFor="bybitTestnetApiKey">Bybit Testnet API Key</Label>
              <Input
                id="bybitTestnetApiKey"
                type="text"
                value={formData.bybitTestnetApiKey}
                onChange={handleChange('bybitTestnetApiKey')}
                placeholder="Enter Bybit testnet API key"
              />
            </div>
            
            <div>
              <Label htmlFor="bybitTestnetApiSecret">Bybit Testnet API Secret</Label>
              <Input
                id="bybitTestnetApiSecret"
                type="password"
                value={formData.bybitTestnetApiSecret}
                onChange={handleChange('bybitTestnetApiSecret')}
                placeholder="Enter Bybit testnet API secret"
              />
            </div>
            
            <div>
              <Label htmlFor="testnetApiKey">Trading Webhook Testnet API Key</Label>
              <Input
                id="testnetApiKey"
                type="text"
                value={formData.testnetApiKey}
                onChange={handleChange('testnetApiKey')}
                placeholder="Enter trading webhook testnet API key"
              />
            </div>
          </div>
          
          <div className="space-y-4">
            <h3 className="font-medium text-gray-700">Production Configuration</h3>
            
            <div>
              <Label htmlFor="bybitProductionApiKey">Bybit Production API Key</Label>
              <Input
                id="bybitProductionApiKey"
                type="text"
                value={formData.bybitProductionApiKey}
                onChange={handleChange('bybitProductionApiKey')}
                placeholder="Enter Bybit production API key"
              />
            </div>
            
            <div>
              <Label htmlFor="bybitProductionApiSecret">Bybit Production API Secret</Label>
              <Input
                id="bybitProductionApiSecret"
                type="password"
                value={formData.bybitProductionApiSecret}
                onChange={handleChange('bybitProductionApiSecret')}
                placeholder="Enter Bybit production API secret"
              />
            </div>
            
            <div>
              <Label htmlFor="productionApiKey">Trading Webhook Production API Key</Label>
              <Input
                id="productionApiKey"
                type="text"
                value={formData.productionApiKey}
                onChange={handleChange('productionApiKey')}
                placeholder="Enter trading webhook production API key"
              />
            </div>
          </div>
          
          <div className="space-y-4">
            <h3 className="font-medium text-gray-700">Proxy Configuration (Optional)</h3>
            
            <div>
              <Label htmlFor="proxyHost">Proxy Host</Label>
              <Input
                id="proxyHost"
                type="text"
                value={formData.proxyHost || ''}
                onChange={handleChange('proxyHost')}
                placeholder="e.g., 91.124.203.120"
              />
            </div>
            
            <div>
              <Label htmlFor="proxyPort">Proxy Port</Label>
              <Input
                id="proxyPort"
                type="number"
                value={formData.proxyPort || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  proxyPort: e.target.value ? parseInt(e.target.value) : undefined
                }))}
                placeholder="e.g., 12323"
              />
            </div>
            
            <div>
              <Label htmlFor="proxyUsername">Proxy Username</Label>
              <Input
                id="proxyUsername"
                type="text"
                value={formData.proxyUsername || ''}
                onChange={handleChange('proxyUsername')}
                placeholder="Enter proxy username"
              />
            </div>
            
            <div>
              <Label htmlFor="proxyPassword">Proxy Password</Label>
              <Input
                id="proxyPassword"
                type="password"
                value={formData.proxyPassword || ''}
                onChange={handleChange('proxyPassword')}
                placeholder="Enter proxy password"
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};